import Section from '@widgets/components/section';
import ValueWithSign from '@widgets/table/components/value-with-sign';
import UserIndicator from '@widgets/components/user-indicator';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { InputCheckbox } from '@shared/uikit/input-checkbox';
import IconDownload from '@shared/icons/IconDownload';
import { BlueTransparentButton, GrayButton, RedTransparentButton } from '@shared/uikit/button';
import MultiSelect, { ISelectOption } from '@widgets/multi-select';
import { ListLoader, Loader } from '@shared/uikit/loader';
import SectionHeader from '@widgets/components/section-header';
import CustomRangeDatePicker from '@widgets/range-date-picker-with-modes';
import { selectLastUploadedReportAt } from '@app/redux/features/userSlice';
import EmptyList from '@widgets/empty-list';
import SingleSelect from '@widgets/single-select';
import { useUpdateCustomer } from '@app/queries/customer/useUpdateCustomer';
import { toast } from 'react-toastify';
import useCustomerOptions from '@pages/Customer/hooks/useCustomerOptions';
import useCustomDateShortcuts from '@app/hooks/useCustomDateShortcuts';
import { TextInput } from '@shared/uikit/input-text';
import { toIsoDate } from '@app/utils/date';
import IconVerticalThreeDots from '@shared/icons/IconVerticalThreeDots';
import DropdownButton from '@widgets/dropdown-button';
import { DeletePrompt } from '@widgets/prompt';
import IconKanbanUser from '@shared/icons/IconKanbanUser';
import { useAccount } from '@app/queries/account/useAccount';
import { IAccount } from '@entities/account/IAccount';
import useAccountBreadcrumbs from '@pages/Account/hooks/useAccountBreadcrumbs';
import { useEditAccount } from '@app/queries/account/useEditAccount';
import { useDeleteAccount } from '@app/queries/account/useDeleteAccount';
import useAccountGraph from '@pages/Account/hooks/useAccountGraph';
import AccountLinesChart from '@pages/Account/components/line-chart';
import IconWrapper from '@shared/uikit/IconWrapper';
import AccountPopupEdit, { IAccountFormValuesEdit } from '@pages/Account/Edit';
import { ServerError } from '@app/error/ServerError';
import { useCreateCustomer } from '@app/queries/customer/useCreateCustomer';
import { ACCOUNT_PERMISSIONS } from '@app/contstants/permissions';
import PermissionGuard from '@shared/security/PermissionGuard';
import { useUnsavedChanges } from '@app/hooks/useUnsavedChanges';
import { ToggleSwitch } from '@shared/uikit/toggle-switch';
import { useSwitchAccountIsNew } from '@app/queries/account/useSwitchAccountIsNew';
import { useBulkAssignClient } from '@app/queries/account/useBulkAssignClient';
import { ValidationError } from '@app/error/ValidationError';
import { CreateClientPopup } from '@pages/Customer/Create';
import { useCallback } from 'react';
import AddAnotherClientPopup, {
  AnotherClientsTopBar,
} from '@pages/Account/Show/AddAnotherClientPopup';

const AccountShow = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /*
        Selectors
     */
  const reportLastSyncDate = useSelector(selectLastUploadedReportAt);

  /*
        Fetchers
     */
  const accountFetcher = useAccount();
  const editAccountFetcher = useEditAccount();
  const customerUpdateFetcher = useUpdateCustomer();
  const deleteAccountFetcher = useDeleteAccount();
  const createCustomerFetcher = useCreateCustomer();
  const editCustomerFetcher = useUpdateCustomer();
  const switchIsNewFetcher = useSwitchAccountIsNew();
  const bulkAssignClientFetcher = useBulkAssignClient();

  /*
        State
     */
  const [account, setAccount] = useState<IAccount | null>(null);

  const [overrideName, setOverrideName] = useState<string>('');
  const [clientId, setClientId] = useState<ISelectOption | null>(null);
  const [tags, setTags] = useState<ISelectOption | null>(null);
  const [sources, setSources] = useState<ISelectOption | null>(null);
  const [managers, setManagers] = useState<ISelectOption[] | null>(null);
  const [terms, setTerms] = useState<number | string>('0');
  const [clientOutOfFlex, setClientOutOfFlex] = useState<boolean>(false);
  const [isNew, setIsNew] = useState<boolean>(false);
  const [isEditing, setEditing] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isShownEditPopup, showEditPopup] = useState(false);

  const [filters, setFilters] = useState<{
    dateFrom: string;
    dateTo: string;
  } | null>(null);

  const [isShowDeleteAccounts, setShowDeleteAccounts] = useState<boolean>(false);
  const [isShowCreatePopup, setShowCreatePopup] = useState<boolean>(false);
  const [isAnotherClientPopupOpen, setAnotherClientPopupOpen] = useState(false);
  /*
        Hooks
     */
  useAccountBreadcrumbs(account);
  useUnsavedChanges(isEditing);

  const [loadingGraphData, graphData] = useAccountGraph(id, filters);

  const [loadingOptions, sourceOptions, managerOptions, tagOptions] = useCustomerOptions();

  const [dateShortcuts, defaultDateFrom, defaultDateTo] = useCustomDateShortcuts();

  const accountActions: ISelectOption[] = [
    { value: 'edit', label: 'Редактировать' },
    { value: 'remove', label: 'Удалить' },
  ];

  const handleAccountAction = (value: string) => {
    switch (value) {
      case 'edit':
        setEditing(true);
        break;
      case 'remove':
        setShowDeleteAccounts(true);
        break;
      default:
        return;
    }
  };

  /*
        Effects
     */

  useEffect(() => {
    (async () => {
      if (!id) return;

      const account = await accountFetcher.fetch(id);
      setAccount(account);
    })();
  }, [id]);

  useEffect(() => {
    (async () => {
      if (!account) return;

      if (account.client_tags)
        setTags({
          value: account.client_tags,
          label: account.client_tags,
        });
      if (account.client_source)
        setSources({
          value: account.client_source,
          label: account.client_source,
        });
      if (account.managers?.length) {
        setManagers(
          account.managers.map(manager => ({
            value: manager.value,
            label: manager.label,
          }))
        );
      }
      if (account.client_out_of_flex) setClientOutOfFlex(account.client_out_of_flex);
      if (account.override_is_new) setIsNew(account.override_is_new);
      if (account.terms) setTerms(account.terms);
      if (account.account) setOverrideName(account.account);

      let minDate = account.min_date;
      // @ts-ignore
      let dateFrom = minDate ? toIsoDate(new Date(minDate.split('-')?.reverse())) : defaultDateFrom;
      setFilters({
        dateFrom: dateFrom,
        dateTo: defaultDateTo,
      });
    })();
  }, [account]);

  const resetStatesData = () => {
    if (!account) return;
    setTags(account.tags);
    setSources(account.source);
    setManagers(
      account.managers.map(manager => ({
        value: manager.value,
        label: manager.label,
      }))
    );
    setTerms(account.terms);
  };

  /*
        Handlers
     */
  const updateAccount = async () => {
    if (!id || !account || !managers?.length) return;

    try {
      await editAccountFetcher.fetch({
        accountType: {
          value: account.account_type,
          label: account.account_type,
        },
        account: account.account,
        managerId: managers[0],
        regionId: { value: account.region, label: account.region },
        clientId: account.client,
        agents: account.agents.map(item => ({
          account: { value: item.account, label: item.account },
          cashback: item.cashback,
        })),
        discount: account.mining_terms?.cashback !== undefined ? account.mining_terms.cashback : 0,
        cashback: account.mining_terms?.discount !== undefined ? account.mining_terms.discount : 0,
      });

      if (isNew != account.override_is_new) {
        await switchIsNewFetcher.fetch({ account: account.account, is_new: isNew });
      }

      await customerUpdateFetcher.fetch({
        clientId: account.client_id,
        managers: managers,
        tag: tags,
        source: sources,
        terms: terms ? +terms : null,
        outOfFlex: clientOutOfFlex,
        isNew: isNew,
      });
      setEditing(false);
      setIsSubmitting(false);
      toast.success('Изменения сохранены');
    } catch (e) {
      toast.error('Произошла ошибка при сохранении изменений');
    }
  };

  const onEdit = async (data: IAccountFormValuesEdit) => {
    try {
      let clientId = data.clientId;
      if (clientId?.value === 'create-new-client') {
        clientId = await createCustomerFetcher.fetch(
          // @ts-ignore
          {
            managers: [data.managerId],
            name: `${data.newClientName}`,
            source: data.sourceId,
            tag: data.tagId,
            terms: +data.clientPercent,
          }
        );
      } else {
        // @ts-ignore
        await editCustomerFetcher.fetch({
          managers: [data.managerId],
          source: data.sourceId,
          tag: data.tagId,
          clientId: clientId.value,
        });
      }

      await editAccountFetcher.fetch({
        accountType: data.accountType,
        regionId: data.regionId,
        account: data.accountName,
        clientId: clientId,
        managerId: data.managerId,
        is_processed: data.is_processed,
        cashback: data.cashback,
        discount: data.discount,
        agents: data.agents,
      });

      showEditPopup(false);
      toast.success('Изменения сохранены');
    } catch (error: any) {
      if (error instanceof ServerError) toast.error(error.error);
    }
  };

  const onCreate = useCallback(
    async (
      managerId: ISelectOption | null,
      regionId: ISelectOption | null,
      tagId: ISelectOption | null,
      sourceId: ISelectOption | null,
      clientName: string | null,
      clientPercent: string | null
    ) => {
      if (!managerId || !regionId || !sourceId || !clientName || !clientPercent) {
        throw new ValidationError([]);
      }
      if (!id) return;

      const newClient = await createCustomerFetcher.fetch({
        managers: [managerId],
        name: `${clientName}`,
        source: sourceId,
        tag: tagId,
        terms: +clientPercent,
        region: regionId,
      });
      const accountIds = account?.account ? [account.account] : [id];
      await bulkAssignClientFetcher.fetch({
        accounts: accountIds,
        client: typeof newClient.value === 'number' ? newClient.value : Number(newClient.value),
        manager: typeof managerId.value === 'number' ? managerId.value : Number(managerId.value),
      });
      const updated = await accountFetcher.fetch(id);
      setAccount(updated);
      setShowCreatePopup(false);
      toast.success('Клиент создан и привязан к аккаунту');
    },
    [id, account?.account, createCustomerFetcher, bulkAssignClientFetcher, accountFetcher]
  );

  const onDeleteAccount = async () => {
    if (!id) return;

    try {
      await deleteAccountFetcher.fetch({ account: id });

      setShowDeleteAccounts(false);
      toast.success('Клиент успешно удален');

      navigate('/via-btc/account');
    } catch {
      toast.error('Произошла ошибка при удалении');
    }
  };

  return (
    <>
      {!loadingOptions && accountFetcher.isSuccess ? (
        <>
          <Section>
            <div className="text-[24px] flex items-center justify-between">
              <div className="flex gap-[20px] items-center text-[20px]">
                {isEditing ? (
                  <TextInput
                    value={overrideName}
                    width="200px"
                    height="40px"
                    bgColor="#211F24"
                    borderColor="#211F24"
                    onChange={(value: string) => setOverrideName(value)}
                  />
                ) : (
                  <div
                    className={`h-[40px] rounded-[8px] text-[20px] bg-[#141316] px-[10px] flex items-center border border-solid border-[#2B292F]`}
                  >
                    <ValueWithSign
                      containerClassName="[&>*:first-child]:mt-[7px]"
                      value={account?.account}
                      color="bg-[#99840F]"
                    />
                  </div>
                )}
                {account?.client_name ? (
                  <div
                    className={`h-[40px] rounded-[8px] text-[20px] bg-[#141316] px-[10px] flex items-center border border-solid border-[#2B292F]`}
                  >
                    <ValueWithSign
                      containerClassName="[&>*:first-child]:mt-[7px]"
                      value={account?.client_name}
                      color="bg-[#4598E6]"
                    />
                  </div>
                ) : null}
                {account ? (
                  <div className="flex items-center min-h-[40px] min-w-0">
                    <AnotherClientsTopBar items={account.another_clients} />
                  </div>
                ) : null}
                {account?.client != null ? (
                  <PermissionGuard permissions={ACCOUNT_PERMISSIONS.EDIT}>
                    <button
                      type="button"
                      className="cursor-pointer h-[40px] rounded-[8px] text-[20px] bg-[#141316] px-[10px] flex items-center border border-solid border-[#2B292F] shrink-0"
                      onClick={() => setAnotherClientPopupOpen(true)}
                    >
                      <p className="text-sm text-[#959595] hover:text-white whitespace-nowrap">
                        + доп. клиент
                      </p>
                    </button>
                  </PermissionGuard>
                ) : (
                  <button
                    type="button"
                    className="cursor-pointer h-[40px] rounded-[8px] text-[20px] bg-[#141316] px-[10px] flex items-center border border-solid border-[#2B292F] shrink-0"
                    onClick={() => setShowCreatePopup(true)}
                  >
                    <p className="text-sm text-[#959595] hover:text-white whitespace-nowrap">
                      + добавить клиента
                    </p>
                  </button>
                )}
              </div>
              <div className="flex gap-[10px] text-[14px] text-[white] ">
                <div className="flex gap-[5px] items-center">
                  <InputCheckbox
                    label=""
                    onChange={(value: boolean) => {
                      if (isEditing) setClientOutOfFlex(value);
                    }}
                    value={clientOutOfFlex}
                  />
                  <span
                    className={`text-[12px]  text-[#FFFFFF] ${isEditing ? '' : 'opacity-[.4]'} `}
                  >
                    Вне бонуса
                  </span>
                </div>

                {account ? (
                  <>
                    <ToggleSwitch
                      label="Новый клиент"
                      onChange={(value: boolean) => {
                        if (isEditing) setIsNew(value);
                      }}
                      value={isNew}
                    />
                  </>
                ) : null}

                {isEditing ? (
                  <MultiSelect
                    width={180}
                    value={managers ? managers : []}
                    title="Менеджер"
                    showSelections
                    isLoading={loadingOptions}
                    options={managerOptions}
                    onChange={values => {
                      setManagers(values);
                    }}
                  />
                ) : (
                  <div
                    className={`h-[33px] min-w-[180px] rounded-[8px] text-[12px] bg-[#141316] px-[10px] flex items-center border border-solid border-[#2B292F]`}
                  >
                    <IconWrapper>
                      <IconKanbanUser />
                    </IconWrapper>
                    <span className="ml-[5px]">
                      {managers?.map(value => value.label).join(', ')}
                    </span>
                  </div>
                )}

                <GrayButton
                  onClick={() => console.log()}
                  title={
                    <div className="flex gap-[10px] items-center fill-[white]">
                      <IconDownload className="w-[11px] h-[14px] fill-[#D9D9D9]" />
                    </div>
                  }
                />
                <PermissionGuard permissions={ACCOUNT_PERMISSIONS.EDIT}>
                  <DropdownButton
                    title={<IconVerticalThreeDots iconWithBorder={false} />}
                    className="h-[33px] w-[15px]"
                    options={accountActions}
                    leftPx={-100}
                    onSelect={(value: any) => {
                      handleAccountAction(value);
                    }}
                  />
                </PermissionGuard>
              </div>
            </div>

            <div className="grid md:grid-cols-5 gap-[15px] my-[20px]">
              <UserIndicator
                title="Общий хешрейт"
                value={account?.hashrate !== undefined ? account?.hashrate + ' Ph' : '-'}
              />

              <PermissionGuard permissions={ACCOUNT_PERMISSIONS.VIEW_BTC}>
                <UserIndicator
                  title="Общий доход в BTC"
                  value={account?.reward !== undefined ? account?.reward : '-'}
                />
              </PermissionGuard>

              <div
                className={`p-[20px] relative solid rounded-[16px] w-full ${isEditing ? 'bg-[#211F24]' : 'border border-opacity-30  border-[#959595]'} `}
              >
                <div className="text-[#777777]">Условия на ViaBTC</div>
                <div className="text-[32px] gap-[10px] flex flex-wrap items-center">
                  {!isEditing ? (
                    <>{terms !== null ? terms + '%' : '-'}</>
                  ) : (
                    <div className="w-full mt-[10px] flex items-center gap-[10px]">
                      <TextInput
                        width="100%"
                        value={`${terms}%`}
                        bgColor="#211F24"
                        borderColor="#211F24"
                        onChange={(value: string) => setTerms(value.slice(0, -1))}
                      />
                    </div>
                  )}
                </div>
              </div>

              <UserIndicator
                title="Дней на ViaBTC"
                value={account?.days !== undefined ? account?.days : '-'}
              />
              <UserIndicator
                title="% Interhash"
                value={
                  account?.referral_interest !== undefined ? account?.referral_interest + '%' : '-'
                }
              />
            </div>

            <div className="grid md:grid-cols-5 gap-[15px] my-[20px] mb-[5px]">
              <div className="w-[100%]">
                {isEditing ? (
                  <>
                    <div className="mb-[10px]">Источник</div>
                    <SingleSelect
                      title="Не выбрано"
                      value={sources}
                      isLoading={false}
                      options={sourceOptions}
                      width="100%"
                      onChange={value => setSources(value)}
                    />
                  </>
                ) : (
                  <>
                    <>
                      <div className="mb-[10px]">Источник</div>
                      <div
                        className={`h-[33px] w-full rounded-[8px] text-[12px] bg-[#141316] px-[10px] flex items-center border border-solid border-[#2B292F]`}
                      >
                        <span>{sources?.value}</span>
                      </div>
                    </>
                  </>
                )}
              </div>

              <div className="w-[100%]">
                {isEditing ? (
                  <>
                    <div className="mb-[10px]">Тэг</div>
                    <SingleSelect
                      title="Не выбрано"
                      value={tags}
                      isLoading={false}
                      options={tagOptions}
                      width="100%"
                      onChange={value => setTags(value)}
                    />
                  </>
                ) : (
                  <>
                    <>
                      <div className="mb-[10px]">Тэг</div>
                      <div
                        className={`h-[33px] w-full rounded-[8px] text-[12px] bg-[#141316] px-[10px] flex items-center border border-solid border-[#2B292F]`}
                      >
                        <span>{tags?.value}</span>
                      </div>
                    </>
                  </>
                )}
              </div>

              <div />
              {isEditing ? (
                <>
                  <div className="w-[100%] mt-auto">
                    {!isSubmitting ? (
                      <RedTransparentButton
                        onClick={() => {
                          resetStatesData();
                          setEditing(false);
                        }}
                        title="Отменить"
                      />
                    ) : null}
                  </div>
                  <div className="w-[100%] mt-auto">
                    <BlueTransparentButton
                      onClick={() => {
                        setIsSubmitting(true);
                        if (!isSubmitting) updateAccount();
                      }}
                      title={
                        isSubmitting ? (
                          <Loader size={30} position="center" loading={isSubmitting} />
                        ) : (
                          'Сохранить'
                        )
                      }
                    />
                  </div>
                </>
              ) : (
                <>
                  <div />
                  <div />
                </>
              )}
            </div>

            <div className="mt-8">
              <Section>
                <div className="mb-[30px] flex items-center gap-[20px]">
                  <SectionHeader title="Общий хешрейт" />
                  <CustomRangeDatePicker
                    onChange={values =>
                      setFilters({
                        ...filters,
                        dateFrom: values.startDate,
                        dateTo: values.endDate,
                      })
                    }
                    width={180}
                    shortcuts={dateShortcuts}
                    minDate={filters?.dateFrom ? new Date(filters?.dateFrom) : undefined}
                    /** @ts-ignore */
                    maxDate={
                      reportLastSyncDate
                        ? new Date(reportLastSyncDate.split('.').reverse())
                        : undefined
                    }
                    value={
                      filters
                        ? {
                            startDate: filters.dateFrom,
                            endDate: filters.dateTo,
                          }
                        : undefined
                    }
                  />
                </div>
                {!loadingGraphData ? (
                  <>
                    {graphData.length ? (
                      <>
                        <AccountLinesChart data={graphData} />
                      </>
                    ) : (
                      <div className="mt-[20px]">
                        <EmptyList />
                      </div>
                    )}
                  </>
                ) : (
                  <ListLoader loading={true} />
                )}
              </Section>
            </div>
          </Section>
          {/*
            <accountActions.Component />
            */}
        </>
      ) : (
        <ListLoader loading={true} />
      )}
      {isShowDeleteAccounts ? (
        <DeletePrompt
          onClose={() => {
            setShowDeleteAccounts(false);
          }}
          onSubmit={onDeleteAccount}
          open={true}
          title={<span className="text-[18px] text-[white] text-center">Удалить аккаунт?</span>}
          subTitle={<>Аккаунт будет удалён из базы данных.</>}
        />
      ) : null}
      {isShowCreatePopup ? (
        <CreateClientPopup
          open={true}
          onClose={() => setShowCreatePopup(false)}
          onSubmit={onCreate}
        />
      ) : null}
      {account?.client != null ? (
        <AddAnotherClientPopup
          open={isAnotherClientPopupOpen}
          onClose={() => setAnotherClientPopupOpen(false)}
          accountLogin={account.account}
          onUpdated={async () => {
            if (!id) return;
            const updated = await accountFetcher.fetch(id);
            setAccount(updated);
          }}
        />
      ) : null}
    </>
  );
};

export default AccountShow;
